import React from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Segmented } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

/**
 * Format a stats `{ from, to }` window as "May 1, 2026 → May 18, 2026"
 * (or "All time" when both are null). Exported so PartnerStats / GlobalStats
 * can echo the same string inside each section title.
 */
export function formatStatsRange(range) {
  if (!range || (!range.from && !range.to)) return 'All time';
  const from = range.from ? dayjs(range.from).format('MMM D, YYYY') : '…';
  const to = range.to ? dayjs(range.to).format('MMM D, YYYY') : '…';
  return `${from} → ${to}`;
}

const { RangePicker } = DatePicker;

/**
 * Stats filter strip — preset period + optional custom range + always-visible
 * resolved date window. Used on PartnerStats, GlobalStats, and the partner-
 * viewer Stats tab so all three surfaces share the same UX.
 *
 * Controlled component. `value = { period, from, to }` where:
 *   - `period`: preset id ('today' | 'this_week' | 'last_week' | 'previous_month' |
 *     'this_month' | 'all' | 'custom'). 'custom' means use { from, to }.
 *   - `from`, `to`: ISO date strings (YYYY-MM-DD), only meaningful when
 *     period === 'custom'.
 *
 * `resolvedRange = { from, to }` (ISO strings or null) is what the BACKEND
 * returned for this query — passed back here so the always-visible label
 * shows what the server actually applied (handles edge cases like 'today'
 * across timezones).
 */
const PERIOD_OPTIONS_DEFAULT = [
  { value: 'today' },
  { value: 'this_week' },
  { value: 'last_week' },
  { value: 'this_month' },
  { value: 'previous_month' },
  { value: 'all' },
  { value: 'custom' },
];

export default function StatsFilters({ value, onChange, resolvedRange, periodOptions, rightSlot }) {
  const { t } = useTranslation('partners');
  const opts = (periodOptions || PERIOD_OPTIONS_DEFAULT).map((o) => ({
    value: o.value,
    label: o.label || t(`periods.${o.value}`, { defaultValue: prettyPeriodLabel(o.value) }),
  }));

  const handlePresetChange = (next) => {
    if (next === 'custom') {
      // Default custom range to last 30 days when user picks Custom for the first time.
      const today = dayjs();
      const from = value.from || today.subtract(29, 'day').format('YYYY-MM-DD');
      const to = value.to || today.format('YYYY-MM-DD');
      onChange({ period: 'custom', from, to });
    } else {
      onChange({ period: next, from: undefined, to: undefined });
    }
  };

  const handleRangeChange = (range) => {
    if (!range || range.length < 2 || !range[0] || !range[1]) return;
    onChange({
      period: 'custom',
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    });
  };

  const rangePickerValue =
    value.period === 'custom' && value.from && value.to ? [dayjs(value.from), dayjs(value.to)] : null;

  const labelText = formatStatsRange(resolvedRange);

  return (
    <div className="StatsFilters" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <Segmented options={opts} value={value.period} onChange={handlePresetChange} />
        {value.period === 'custom' && (
          <RangePicker value={rangePickerValue} onChange={handleRangeChange} allowClear={false} />
        )}
        {rightSlot}
      </div>
      {/* Prominent range chip — the one figure on the page that has to be
          unmistakable. Echoed inline next to each section title so the
          context never gets lost as the operator scrolls. */}
      <div
        className="stats-range-chip"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          alignSelf: 'flex-start',
          padding: '8px 14px',
          borderRadius: 999,
          background: 'var(--accent-strong)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: 0.2,
          boxShadow: '0 1px 3px rgba(22,119,255,0.25)',
        }}
      >
        <CalendarOutlined />
        <span>{labelText}</span>
      </div>
    </div>
  );
}

function prettyPeriodLabel(v) {
  switch (v) {
    case 'today':
      return 'Today';
    case 'this_week':
      return 'This week';
    case 'last_week':
      return 'Last week';
    case 'this_month':
      return 'This month';
    case 'previous_month':
      return 'Previous month';
    case 'all':
      return 'All';
    case 'custom':
      return 'Custom';
    default:
      return v;
  }
}
